import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatMenuModule } from '@angular/material/menu';
import { DevExtremeThemeService } from '@backoffice-frontend/shared/devextreme';
import { TailwindThemeService } from '@backoffice-frontend/shared/ui-tailwind';
import { LanguageService } from '@backoffice-frontend/shared/util-transloco';
import { AccountService } from '@clean-code/shared/auth/util-auth';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faCheck,
  faChevronDown,
  faMoon,
  faSun,
} from '@fortawesome/pro-solid-svg-icons';
import { TranslocoModule } from '@jsverse/transloco';
import { map, of, startWith } from 'rxjs';

export type FlagResolver = (langId: string | null | undefined) => string;

const defaultFlagResolver: FlagResolver = (langId) =>
  `./assets/img/Flag_${(langId || 'de').toLowerCase()}.svg`;

@Component({
  selector: 'cc-sidenav-controls',
  templateUrl: './sidenav-controls.component.html',
  imports: [CommonModule, MatMenuModule, FontAwesomeModule, TranslocoModule],
})
export class SidenavControlsComponent {
  @Input() flagFor: FlagResolver = defaultFlagResolver;
  @Input() persistLanguageToAccount = false;

  public readonly languageService: LanguageService = inject(LanguageService);
  private readonly tailwindThemeService: TailwindThemeService =
    inject(TailwindThemeService);
  private readonly devExtremeThemeService: DevExtremeThemeService =
    inject(DevExtremeThemeService);
  private readonly accountService: AccountService = inject(AccountService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  public readonly faChevronDown = faChevronDown;
  public readonly faCheck = faCheck;
  public readonly faSun = faSun;
  public readonly faMoon = faMoon;

  public readonly toggleControl = new FormControl(
    this.tailwindThemeService.isDarkMode,
  );

  public readonly darkMode$ = this.toggleControl.valueChanges.pipe(
    startWith(this.toggleControl.value),
  );

  public readonly langs$ = of(this.languageService.configuredLanguages).pipe(
    map((langs) => langs.map((x) => x as { id: string; label: string })),
  );

  constructor() {
    this.toggleControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((darkMode) => {
        this.tailwindThemeService.changeTheme(darkMode);
        this.devExtremeThemeService.changeTheme(darkMode);
      });
  }

  setDarkMode(darkMode: boolean): void {
    if (this.toggleControl.value !== darkMode) {
      this.toggleControl.setValue(darkMode);
    }
  }

  setLanguage(id: string | MatButtonToggleChange): void {
    const lang =
      typeof id === 'string' || id instanceof String
        ? (id as string)
        : id.value;

    if (this.languageService.currentLocale !== lang) {
      if (this.persistLanguageToAccount) {
        this.accountService.changeLanguage(lang);
      } else {
        this.languageService.setLanguage(lang);
      }
    }
  }
}
